<script setup lang="ts">
	useHead({
		meta: [
			{ name: 'geo.placename', content: 'Am Neuen Palais, 14469 Potsdam, Germany'},
			{ name: 'geo.position', content: '52.398971,13.012558'},
			{ name: 'geo.region', content: 'DE-BB'},
			{ name: 'theme-color', content: '#1e325c'},
			{ name: 'msapplication-TileColor', content: '#1e325c'},
			{ property: 'og:type', content: 'website'},
			{ property: 'og:locale', content: 'de_DE'}
		],
		link: [
			{ rel: 'icon', type: 'image/png', sizes: '16x16', content: usePublicUrl('/icons/favicon-16x16.png')},
			{ rel: 'icon', type: 'image/png', sizes: '32x32', content: usePublicUrl('/icons/favicon-32x32.png')},
			{ rel: 'icon', type: 'image/png', sizes: '192x192', content: usePublicUrl('/icons/android-chrome-192x192.png')},
			{ rel: 'icon', type: 'image/png', sizes: '512x512', content: usePublicUrl('/icons/android-chrome-512x512.png')},
			{ rel: 'apple-touch-icon', content: usePublicUrl('/icons/apple-touch-icon.png')},
			{ rel: 'msapplication-TileImage', content: usePublicUrl('/icons/tileImage-144x144.png')}
		]
	})

	const rConf = useRuntimeConfig()

	const profile = ref({})
	const cachedData = useNuxtData('profile')
	if (cachedData.data.value) {
		profile.value = cachedData.data.value
	} else {
		const { data } = await useFetch('/rest/profiles', {
			baseURL: rConf.public.apiBase,
			key: 'profile'
		})
		if(data.value && data.value.info.count === 1) {
			profile.value = data.value.items[0]
		}
	}

	const run = ref({})
	if(profile.value && profile.value.current_run_pk){
		let apiUrlRun = computed(() => `/rest/run?run=${profile.value.current_run_pk}`)

		const cachedRunData = useNuxtData('current-run')
		if (cachedRunData.data.value) {
			run.value = cachedRunData.data.value
		} else {
			const { data } = await useFetch(apiUrlRun, {
				baseURL: rConf.public.apiBase,
				key: 'current-run'
			})
			if(data.value) {
				run.value = data.value
			}
		}
	}

	const lastRun = ref({})
	if(profile.value && profile.value.last_run_pk){
		let apiUrlLastRun = computed(() => `/rest/run?run=${profile.value.last_run_pk}`)

		const cachedLastRunData = useNuxtData('last-run')
		if (cachedLastRunData.data.value) {
			lastRun.value = cachedLastRunData.data.value
		} else {
			const { data } = await useFetch(apiUrlLastRun, {
				baseURL: rConf.public.apiBase,
				key: 'last-run'
			})
			if(data.value) {
				lastRun.value = data.value
			}
		}
	}

	const sponsors = ref({})
	const cachedDataSponsors = useNuxtData('sponsors')
	if (cachedDataSponsors.data.value) {
		sponsors.value = cachedDataSponsors.data.value
	} else {
		const { data } = await useFetch('/rest/sponsors', {
			baseURL: rConf.public.apiBase,
			query: { sort: 'position' },
			key: 'sponsors'
		})
		if(data.value) {
			sponsors.value = data.value
		}
	}

	const adsTitleSponsor = ref({})
	const cachedDataAdsTitleSponsor = useNuxtData('ads-title-sponsor')
	if (cachedDataAdsTitleSponsor.data.value) {
		adsTitleSponsor.value = cachedDataAdsTitleSponsor.data.value
	} else {
		const { data } = await useFetch('/rest/ads', {
			baseURL: rConf.public.apiBase,
			query: { is_active: true, is_title_sponsor: true },
			key: 'ads-title-sponsor'
		})
		if(data.value) {
			adsTitleSponsor.value = data.value
		}
	}

	const ads = ref({})
	const cachedDataAds = useNuxtData('ads')
	if (cachedDataAds.data.value) {
		ads.value = cachedDataAds.data.value
	} else {
		const { data } = await useFetch('/rest/ads', {
			baseURL: rConf.public.apiBase,
			query: { is_active: true, is_title_sponsor: false },
			key: 'ads'
		})
		if(data.value) {
			ads.value = data.value
		}
	}

	//const { data: navigationItems } = await useAsyncData(() => $fetch('/data/navigation.json?v=011025'))
	//const { data: footerLinkGroups } = await useAsyncData(() => $fetch('/data/footer-link-groups.json?v=011025'))
	const { data: footerLinks } = await useAsyncData(() => $fetch('/data/footer-navigation.json?v=011025'))

	/*if(run.value?.title){
		navigationItems.value.splice(1, 0, { "label": `${run.value.title}`, "name": "run-detail", "title": `Alles zum ${run.value.title}`}).join()
	}*/

	const header = ref(null)
	const isScrolled = ref<boolean>(false)

	const route = useRoute();
	const drawerOpen = ref(false)

	onMounted(() => {
		useEventListener(window, 'scroll', function(){
			if(header.value){
				isScrolled.value = window.scrollY > header.value.clientHeight
			}
		})
	})

	watch(route, value => {
		if(drawerOpen.value) {
			drawerOpen.value = false
		}
  	}, {deep: true, immediate: true})
</script>

<template>
	<GsApp color-mode-preference="light">
		<NuxtLayout name="site-default">
			<template #header>
				<div v-if="$screen.lt.lg" ref="header" class="py-2.5 bg-white transition duration-300 ease-in-out" :class="{'drop-shadow-xl z-50':isScrolled}">
					<div class="site-grid">
						<div class="flex justify-between">
							<div class="w-[110px] xs:w-[135px] sm:w-[150px] md:w-[160px] lg:w-[200px] my-auto pe-1.5 xs:pe-0" :class="{'lg:!w-[110px]':isScrolled}">
								<NuxtLink :to="{name: 'home'}" class="block" title="zur Startseite">
									<img v-lazy-load  data-src="@/assets/img/logo-firmenlauf-potsdam.svg" alt="Logo Firmenlauf Potsdam" class="w-full" />
								</NuxtLink>
							</div>
							<div class="flex justify-end w-full">
								<div class="hidden sm:flex sm:gap-2 items-center justify-end">
									<ProfileRegistrationBtn></ProfileRegistrationBtn>
									<ProfileResultBtn></ProfileResultBtn>
								</div>

								<div v-if="$screen.lt.lg" class="flex">
									<UDrawer direction="left" v-model:open="drawerOpen">
										 <UButton :square="true" variant="solid" class="rounded-full p-1.5 md:p-3.5 my-auto bg-white/50 hover:bg-yellow-sea-500/25 cursor-pointer" title="Navigation öffnen" aria-label="Navigation öffnen">
											 <GsIcon name="fa6-solid:bars" class="size-8 md:size-10 text-black hover:text-primary"></GsIcon>
										 </UButton>
										 <template #header>
											<h2 class="sr-only">Navigation</h2>
										</template>
										<template #body>
											<div class="w-[260px] xs:w-[320px] sm:w-[380px] h-full relative">
												<ProfileNavigationMobile :footer-items="footerLinks.slice(0, footerLinks.length -1)" />
											</div>
										</template>
									</UDrawer>
								</div>
							</div>
						</div>
					</div>
				</div>
			</template>

			<div class="pt-[100px] xs:pt-[110px] sm:pt-[120px] md:pt-[150px] lg:pt-0">
				<NuxtPage/>
			</div>

			<template #footer>
				<div class="site-grid">
					<div class="grid grid-cols-12">
						<div :class="{'col-span-9 col-start-4 xl:col-start-3' : $screen.gte.lg, 'col-span-12' : $screen.lt.lg}">
							<div class="border-t-[3px] border-zinc-300 pt-8 sm:pt-12 lg:pt-16 xl:pt-24 px-4">
								<div class="grid grid-cols gap-12">
									<div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 md:mb-16">
										<ProfileNavigationFooter></ProfileNavigationFooter>
										<div class="text-center md:text-left">
											<NewsletterTeaser></NewsletterTeaser>
										</div>
									</div>
									<div class="w-full flex flex-col sm:flex-row justify-between mb-12 border-t-1 md:border-none border-gray-200 pt-4 md:pt-0">
										<nav v-if="footerLinks && footerLinks.length"  role="navigation" class="flex flex-col md:flex-row justify-center md:justify-start md:space-x-6 md:space-x-12 space-y-4 md:space-y-0">
											<NuxtLink v-for="(item,idx) in footerLinks" :to="{name: item.name, params: item.slug ? { article_slug: item.slug } : {}}" no-prefetch class="text-neutral-400 text-center sm:text-left hover:text-black transition duration-300 ease-in-out" :title="item.title">
												{{ item.label }}
											</NuxtLink>
										</nav>
										<a href="https://www.markov-markov.de" target="_blank" class="flex group mt-16 sm:mt-0 xl:-mr-0" title="Agentur Website">
											<span class="flex mx-auto xs:mx-none">
												<GsIcon name="fa:gears" class="flex-none text-silver-300 group-hover:text-stromboli-700 text-[14px] my-auto me-2 size-4 xl:size-5" />
												<span class="my-auto text-shooner-500 opacity-60 text-lg font-light leading-9 group-hover:text-primary">markov&markov</span>
											</span>
										</a>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</template>
		</NuxtLayout>
	</GsApp>
</template>

<style>
	.site-grid {
		--padding-inline: theme('padding.4');
		--content-max-width: 1900px;
		--breakout-max-width: 100%;
	}
	header {
		position: fixed;
		left: 0;
		top:0;
		width: 100%;
		z-index: 40;
	}
	header .router-link-active,
	nav .router-link-active {
		color: var(--color-accent);
	}
	footer ul a.router-link-active {
		color: var(--color-accent);
	}
</style>
